import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  MapPin,
  CalendarDays,
  MessageCircle,
  ArrowRight,
  ArrowLeft,
  Check,
  Truck,
  Boxes,
  Snowflake,
  Container,
  Wrench,
} from "lucide-react";
import { WHATSAPP_URL } from "@/lib/constants";

const GOODS = [
  { id: "pallet", label: "Pallet / Bancali", icon: Boxes },
  { id: "container", label: "Container", icon: Container },
  { id: "sfuso", label: "Merce Sfusa", icon: Package },
  { id: "refrigerato", label: "Refrigerato", icon: Snowflake },
  { id: "industriale", label: "Industriale", icon: Wrench },
  { id: "altro", label: "Altro", icon: Truck },
];

const STEPS = [
  { n: "01", title: "Tipo Merce", icon: Package },
  { n: "02", title: "Tratta", icon: MapPin },
  { n: "03", title: "Data & Dettagli", icon: CalendarDays },
];

export default function PreventivoWizard() {
  const [step, setStep] = useState(0);
  const [goods, setGoods] = useState("");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [date, setDate] = useState("");
  const [details, setDetails] = useState("");

  const canNext = useMemo(() => {
    if (step === 0) return goods !== "";
    if (step === 1) return origin.trim() !== "" && destination.trim() !== "";
    return date !== "";
  }, [step, goods, origin, destination, date]);

  const goodsLabel = GOODS.find((g) => g.id === goods)?.label || "";

  const whatsappHref = useMemo(() => {
    const msg =
      `Ciao Bennici Autotrasporti, vorrei un preventivo:\n\n` +
      `• Tipo merce: ${goodsLabel || "-"}\n` +
      `• Partenza: ${origin || "-"}\n` +
      `• Destinazione: ${destination || "-"}\n` +
      `• Data desiderata: ${date || "-"}\n` +
      (details ? `• Note: ${details}\n` : "") +
      `\nGrazie!`;
    return `${WHATSAPP_URL}?text=${encodeURIComponent(msg)}`;
  }, [goodsLabel, origin, destination, date, details]);

  return (
    <section
      id="preventivo"
      data-testid="preventivo-section"
      className="relative py-24 md:py-36 bg-[#050505] overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 divider-glow" />
      <div className="absolute -top-24 -right-32 h-[420px] w-[420px] rounded-full bg-[#0055ff] opacity-[0.09] blur-[140px]" />
      <div className="absolute bottom-0 left-0 h-[360px] w-[360px] rounded-full bg-[#00e5ff] opacity-[0.05] blur-[140px]" />

      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-blue-400" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-blue-300">
              Preventivo Guidato
            </span>
          </div>
          <h2
            data-testid="preventivo-title"
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-[-0.02em] leading-[1.02] text-white"
          >
            Costruisci il tuo{" "}
            <span className="metal-text-gradient">preventivo</span> in 3 passi.
          </h2>
          <p className="mt-6 text-gray-400 text-base md:text-lg max-w-2xl">
            Compila i campi e riceverai il messaggio precompilato per WhatsApp —
            risposta entro poche ore.
          </p>
        </div>

        {/* Wizard container */}
        <div className="relative border border-white/10 bg-gradient-to-b from-[#0A0D14] to-[#070912]">
          {/* Progress steps */}
          <div className="grid grid-cols-3 border-b border-white/10">
            {STEPS.map((s, i) => {
              const active = i === step;
              const done = i < step;
              return (
                <button
                  key={s.n}
                  type="button"
                  data-testid={`preventivo-step-tab-${i}`}
                  onClick={() => (done ? setStep(i) : null)}
                  className={`relative p-5 md:p-6 text-left transition-colors ${
                    active
                      ? "bg-blue-600/10"
                      : done
                        ? "hover:bg-white/5 cursor-pointer"
                        : "opacity-60 cursor-not-allowed"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-9 w-9 items-center justify-center border transition-colors ${
                        active
                          ? "border-blue-400 bg-blue-500/20 text-blue-300"
                          : done
                            ? "border-blue-500/50 bg-blue-600 text-white"
                            : "border-white/15 text-white/50"
                      }`}
                    >
                      {done ? <Check size={16} /> : <s.icon size={16} />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading text-[10px] tracking-[0.28em] uppercase text-blue-300/70">
                        Step {s.n}
                      </div>
                      <div
                        className={`font-heading text-sm md:text-base font-bold uppercase tracking-tight truncate ${
                          active ? "text-white" : "text-white/70"
                        }`}
                      >
                        {s.title}
                      </div>
                    </div>
                  </div>
                  {active && (
                    <motion.span
                      layoutId="step-underline"
                      className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0055ff] to-[#00e5ff]"
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Step body */}
          <div className="p-6 md:p-10 min-h-[380px] flex flex-col">
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="s0"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  className="flex-1"
                >
                  <label className="text-[11px] uppercase tracking-[0.28em] text-blue-300/80">
                    Che tipo di merce trasporti?
                  </label>
                  <div
                    className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3"
                    data-testid="preventivo-goods-grid"
                  >
                    {GOODS.map((g) => {
                      const selected = goods === g.id;
                      return (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => setGoods(g.id)}
                          data-testid={`preventivo-goods-${g.id}`}
                          className={`group relative p-5 border text-left transition-all ${
                            selected
                              ? "border-blue-400 bg-blue-500/10 shadow-[0_0_20px_rgba(0,85,255,0.35)]"
                              : "border-white/10 hover:border-blue-500/50 bg-black/30"
                          }`}
                        >
                          <g.icon
                            size={26}
                            className={`mb-3 ${
                              selected
                                ? "text-blue-300"
                                : "text-blue-400/70 group-hover:text-blue-300"
                            }`}
                            strokeWidth={2}
                          />
                          <div className="font-heading text-sm font-bold uppercase tracking-tight text-white">
                            {g.label}
                          </div>
                          {selected && (
                            <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center bg-blue-500 text-white">
                              <Check size={12} />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {step === 1 && (
                <motion.div
                  key="s1"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  className="flex-1 grid md:grid-cols-2 gap-6"
                >
                  <div>
                    <label
                      htmlFor="origin"
                      className="text-[11px] uppercase tracking-[0.28em] text-blue-300/80"
                    >
                      Partenza
                    </label>
                    <input
                      id="origin"
                      type="text"
                      value={origin}
                      onChange={(e) => setOrigin(e.target.value)}
                      placeholder="Es. Milano (MI)"
                      data-testid="preventivo-origin"
                      className="mt-3 w-full bg-black/40 border border-white/15 focus:border-blue-400 focus:outline-none px-4 py-4 text-white placeholder:text-white/30 font-body transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="destination"
                      className="text-[11px] uppercase tracking-[0.28em] text-blue-300/80"
                    >
                      Destinazione
                    </label>
                    <input
                      id="destination"
                      type="text"
                      value={destination}
                      onChange={(e) => setDestination(e.target.value)}
                      placeholder="Es. Palermo (PA)"
                      data-testid="preventivo-destination"
                      className="mt-3 w-full bg-black/40 border border-white/15 focus:border-blue-400 focus:outline-none px-4 py-4 text-white placeholder:text-white/30 font-body transition-colors"
                    />
                  </div>
                  <div className="md:col-span-2 mt-2 flex items-center gap-3 text-xs text-gray-500">
                    <MapPin size={14} className="text-blue-400" />
                    Copertura su tutto il territorio nazionale italiano.
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="s2"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  className="flex-1 grid md:grid-cols-2 gap-6"
                >
                  <div>
                    <label
                      htmlFor="date"
                      className="text-[11px] uppercase tracking-[0.28em] text-blue-300/80"
                    >
                      Data desiderata
                    </label>
                    <input
                      id="date"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      data-testid="preventivo-date"
                      className="mt-3 w-full bg-black/40 border border-white/15 focus:border-blue-400 focus:outline-none px-4 py-4 text-white font-body transition-colors [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="details"
                      className="text-[11px] uppercase tracking-[0.28em] text-blue-300/80"
                    >
                      Note (opzionale)
                    </label>
                    <input
                      id="details"
                      type="text"
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      placeholder="Peso, volume, orari, ecc."
                      data-testid="preventivo-details"
                      className="mt-3 w-full bg-black/40 border border-white/15 focus:border-blue-400 focus:outline-none px-4 py-4 text-white placeholder:text-white/30 font-body transition-colors"
                    />
                  </div>

                  {/* Summary preview */}
                  <div className="md:col-span-2 mt-4 border border-blue-500/30 bg-black/40 p-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-blue-300/80 mb-3">
                      Riepilogo
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Package size={14} className="text-blue-400" />
                        <span>{goodsLabel || "—"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <MapPin size={14} className="text-blue-400" />
                        <span className="truncate">
                          {origin || "—"} → {destination || "—"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <CalendarDays size={14} className="text-blue-400" />
                        <span>{date || "—"}</span>
                      </div>
                      {details && (
                        <div className="flex items-center gap-2 text-gray-300 truncate">
                          <span className="text-blue-400">✎</span>
                          <span className="truncate">{details}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Nav buttons */}
            <div className="mt-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="preventivo-back"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 text-xs font-bold uppercase tracking-[0.16em] text-white border border-white/15 hover:border-blue-400 hover:bg-white/5 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/15 disabled:hover:bg-transparent"
              >
                <ArrowLeft size={14} />
                Indietro
              </button>

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={() => canNext && setStep((s) => s + 1)}
                  disabled={!canNext}
                  data-testid="preventivo-next"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-xs font-bold uppercase tracking-[0.16em] text-white bg-[#0055ff] hover:bg-[#0066ff] transition-all shadow-[0_0_20px_rgba(0,85,255,0.5)] disabled:bg-white/10 disabled:shadow-none disabled:cursor-not-allowed"
                >
                  Avanti
                  <ArrowRight size={14} />
                </button>
              ) : (
                <a
                  href={canNext ? whatsappHref : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="preventivo-submit-whatsapp"
                  aria-disabled={!canNext}
                  onClick={(e) => {
                    if (!canNext) e.preventDefault();
                  }}
                  className={`shimmer-btn inline-flex items-center justify-center gap-3 px-8 py-4 text-xs font-bold uppercase tracking-[0.16em] text-white transition-all ${
                    canNext
                      ? "bg-green-600 hover:bg-green-500 shadow-[0_0_25px_rgba(34,197,94,0.6)]"
                      : "bg-white/10 cursor-not-allowed"
                  }`}
                >
                  <MessageCircle size={16} strokeWidth={2.5} />
                  Invia via WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
